import { Headline, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import { ReactComponent as CloudRebrandingIcon } from 'img/icons/cloud_rebranding.svg';
import React, { type FC, memo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkThemeLayout } from 'reactApp/hooks/useDarkTheme';
import { MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import type { MenuItem } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.types';

import { CloseMenuButton } from '../ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import styles from './ScreenNew.css';

interface IProps {
    onClose?: () => void;
    className?: string;
    closable?: boolean;
    withHeader?: boolean;
    withLogo?: boolean;
    contentClassName?: string;
    title?: string;
    info?: string;
    showOptions?: boolean;
    options?: MenuItem[];
    isDarkTheme?: boolean;
}

export const ScreenNew: FC<IProps> = memo(function ScreenNew(props) {
    const {
        onClose,
        closable = true,
        className,
        withHeader,
        withLogo,
        contentClassName,
        children,
        title,
        info,
        showOptions,
        options,
        isDarkTheme,
    } = props;

    useDarkThemeLayout(Boolean(isDarkTheme));

    const rootRef = useRef<HTMLDivElement>(null);

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const navigate = useNavigate();

    const onClickLogo = useCallback(() => {
        navigate('/home');
        onClose?.();
    }, [navigate, onClose]);

    return (
        <div className={classNames(styles.root, className)} ref={rootRef}>
            <div className={classNames(styles.content, contentClassName)}>
                {withHeader && (
                    <div className={styles.header} data-test-id="headers">
                        <div className={styles.leftBlock}>
                            {withLogo && (
                                <CloudRebrandingIcon
                                    width={32}
                                    height={32}
                                    className={styles.logo}
                                    data-test-id="cloud-icon"
                                    onClick={onClickLogo}
                                />
                            )}
                            {title && (
                                <>
                                    <div className={styles.separator} />
                                    <div className={styles.titleContainer}>
                                        <Headline level="1" weight="1" className={styles.title}>
                                            {title}
                                        </Headline>
                                        {info && (
                                            <Text weight="1" className={styles.info}>
                                                {info}
                                            </Text>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className={styles.rightBlock}>
                            {showOptions && options && <MoreMenuButton list={options} />}

                            {closable && <CloseMenuButton onClick={handleClose} withSeparator={false} />}
                        </div>
                    </div>
                )}

                {children}
            </div>
        </div>
    );
});
